import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Trans, useTranslation } from "react-i18next";

import type { PublicPlanCard } from "@/api/queries/publicPlans";
import { pickI18nBlob } from "@/components/admin/I18nField";
import { minorUnitExponent } from "@/lib/money";
import { priceDominatedTiers, recommendPlans, volumeScale, type Minor } from "@/lib/planCost";
import { cx } from "@/lib/utils";

import { formatCompact, formatMoney, formatPercent, moneyParts } from "./pricingFormat";

/**
 * "Which plan is cheapest for a shop my size?"
 *
 * The cards answer half of it. They print a subscription and a take-rate
 * side by side and leave the addition to the reader, so the cheaper
 * subscription looks like the cheaper plan — which it stops being as soon
 * as the commission outgrows the difference. One slider does the addition.
 *
 * Everything is read from ``GET /public/plans``: the rates, the ceilings,
 * the names, even how far the slider reaches. An admin who reprices a
 * tier moves the recommendation without a deploy.
 *
 * Collapsed by default (KAN-1310 design). Only once the visitor sets a
 * volume — slider or typed number — is the winner named and reported
 * through ``onRecommend`` so the page can mark that tier's card. Opening
 * the box is not a volume: the starting value is ours, and "recommended
 * for your volume" on it would be a claim about a number nobody gave
 * (KAN-1947).
 */
export function PlanRecommender({
  plans,
  billing,
  lang,
  vatNote,
  onRecommend,
}: {
  plans: PublicPlanCard[];
  billing: "monthly" | "annual";
  lang: string;
  /** Věta o DPH odvozená z Profilu firmy provozovatele (KAN-1806). */
  vatNote: string;
  onRecommend?: (tier: string | null) => void;
}): JSX.Element | null {
  const { t } = useTranslation("saas");

  const scale = useMemo(() => volumeScale(plans), [plans]);
  const scaleMax = scale.max;
  const [volume, setVolume] = useState<Minor | null>(null);
  const [touched, setTouched] = useState(false);
  // What the visitor is typing, shown as typed until the field loses focus,
  // so thousands separators do not jump under the caret.
  const [draft, setDraft] = useState<string | null>(null);
  const value = volume ?? scale.start;
  const dominated = useMemo(() => priceDominatedTiers(plans, billing), [plans, billing]);

  const { ranked, quotedTiers } = useMemo(
    () => recommendPlans(plans, value, billing),
    [plans, value, billing],
  );
  const winnerTier = ranked[0]?.tier ?? null;

  useEffect(() => {
    if (touched) onRecommend?.(winnerTier);
  }, [touched, winnerTier, onRecommend]);

  // Two priced tiers is the least that can carry a comparison. Below
  // that there is nothing to recommend, and a slider would only imply
  // a choice the visitor does not have.
  if (scaleMax <= 0 || ranked.length < 2) return null;

  const currency = plans[0]?.currency ?? "";
  const unit = 10 ** minorUnitExponent(currency);
  const setFromVisitor = (minor: Minor): void => {
    setVolume(Math.max(0, Math.round(minor)));
    setTouched(true);
  };
  const winner = ranked[0]!;
  const byTier = new Map(plans.map((p) => [p.tier, p]));
  const nameOf = (tier: string): string => {
    const plan = byTier.get(tier);
    return plan ? pickI18nBlob(plan.name_i18n, plan.name, plan.name_en, lang) : tier;
  };
  const verdictValues = {
    plan: nameOf(winner.tier),
    total: formatMoney(winner.totalMinor, currency, lang),
  };
  const fill: CSSProperties = {
    ["--p" as string]: `${(Math.min(value, scaleMax) / scaleMax) * 100}%`,
  };

  return (
    <details className="calcbox" data-testid="plan-recommender">
      <summary>
        <h2>{t("pricingPlans.recommender.heading")}</h2>
        <span className="hint">
          <span>
            {touched ? (
              <Trans
                t={t}
                i18nKey="pricingPlans.recommender.hintPicked"
                values={verdictValues}
                components={{ b: <b /> }}
              />
            ) : (
              t("pricingPlans.recommender.hint")
            )}
          </span>
          <span className="chev" aria-hidden="true" />
        </span>
      </summary>
      <div className="calc">
        <div className="left">
          <div className="grid-bg" aria-hidden="true" />
          <p className="sub">{t("pricingPlans.recommender.lead")}</p>
          <label className="vollabel" htmlFor="plan-recommender-volume">
            {t("pricingPlans.recommender.volumeLabel")}
          </label>
          {/* Typed sums are not bound to the slider's range or steps: a shop
              doing 1,3 mil. a month types it (KAN-1947). */}
          <div className="vol mono">
            <input
              id="plan-recommender-volume-value"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={draft ?? new Intl.NumberFormat(lang).format(Math.round(value / unit))}
              onBlur={() => setDraft(null)}
              onChange={(e) => {
                setDraft(e.target.value);
                const digits = e.target.value.replace(/\D/g, "");
                setFromVisitor(Number(digits || "0") * unit);
              }}
              aria-label={t("pricingPlans.recommender.volumeTyped")}
              data-testid="plan-recommender-volume-value"
            />
            <span className="cur">{moneyParts(0, currency, lang).currency}</span>
          </div>
          <input
            id="plan-recommender-volume"
            type="range"
            min={0}
            max={scaleMax}
            step={scale.step}
            value={Math.min(value, scaleMax)}
            onChange={(e) => setFromVisitor(Number(e.target.value))}
            style={fill}
            data-testid="plan-recommender-volume-input"
            aria-valuetext={formatMoney(value, currency, lang)}
          />
          <div className="ticks" aria-hidden="true">
            <span>{formatCompact(0, currency, lang).text}</span>
            <span>{formatCompact(scaleMax / 2, currency, lang).text}</span>
            <span>{formatCompact(scaleMax, currency, lang).text}</span>
          </div>
          <p
            className="verdict"
            data-testid="plan-recommender-verdict"
            aria-live="polite"
          >
            {touched ? (
              <Trans
                t={t}
                i18nKey={
                  winner.feeCapped
                    ? "pricingPlans.recommender.verdictCapped"
                    : "pricingPlans.recommender.verdict"
                }
                values={verdictValues}
                components={{ b: <b /> }}
              />
            ) : (
              t("pricingPlans.recommender.verdictPending")
            )}
          </p>
        </div>
        <div className="right">
          <div className="rows">
            <ul data-testid="plan-recommender-rows">
              {ranked.map((cost) => {
                const best = touched && cost.tier === winner.tier;
                const percent = formatPercent(
                  byTier.get(cost.tier)?.platform_fee_bps ?? 0,
                  lang,
                );
                return (
                  <li
                    key={cost.tier}
                    data-testid={`plan-recommender-row-${cost.tier}`}
                    className={cx("row", best && "best")}
                  >
                    <span className="rname">
                      {best && <span className="dot" aria-hidden="true" />}
                      {nameOf(cost.tier)}
                    </span>
                    <span className="brk">
                      {cost.subscriptionMinor > 0
                        ? t("pricingPlans.recommender.breakdown", {
                            subscription: formatMoney(
                              cost.subscriptionMinor,
                              currency,
                              lang,
                            ),
                            percent,
                          })
                        : t("pricingPlans.recommender.breakdownNoSubscription", {
                            percent,
                          })}
                      {cost.feeCapped
                        ? ` · ${t("pricingPlans.recommender.capped")}`
                        : ""}
                      {dominated.has(cost.tier) && (
                        <span className="why" data-testid={`plan-recommender-features-${cost.tier}`}>
                          {t("pricingPlans.recommender.forFeatures")}
                        </span>
                      )}
                    </span>
                    <span className="tot">
                      {formatMoney(cost.totalMinor, currency, lang)}
                    </span>
                  </li>
                );
              })}
            </ul>
            {quotedTiers.length > 0 && (
              <div className="quoted" data-testid="plan-recommender-quoted">
                {quotedTiers.map((tier) => (
                  <div key={tier} className="row is-quoted">
                    <span className="rname">{nameOf(tier)}</span>
                    <span className="brk">
                      {t("pricingPlans.recommender.quoted")}
                    </span>
                    <span className="tot">—</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="calcfoot">{t("pricingPlans.recommender.disclaimer", { vat: vatNote })}</p>
        </div>
      </div>
    </details>
  );
}
